import { useCallback } from 'react';
import { NavigateOptions, useLocation, useNavigate } from 'react-router-dom';

/**
 * Like useNavigate, but preserves ?view and ?year from the current URL when
 * opening a child route. This keeps the Finances sub-page context alive while
 * a drawer or sub-page is open, so closing it (navigate(-1)) lands back on the
 * correct sub-page instead of the hub.
 *
 * For absolute URLs, only ?view and ?year are forwarded (they cannot overwrite
 * params already present in the target URL).
 */
const useNavigateKeepingSearch = () => {
  const navigate = useNavigate();
  const { search } = useLocation();

  return useCallback(
    (to: string, options?: NavigateOptions) => {
      const current = new URLSearchParams(search);
      const view = current.get('view');
      const year = current.get('year');

      if (to.startsWith('/')) {
        // Absolute URL: forward view/year only when the target does not set them.
        const [path, qs] = to.split('?');
        const params = new URLSearchParams(qs ?? '');
        if (view && !params.has('view')) params.set('view', view);
        if (year && !params.has('year')) params.set('year', year);
        const newSearch = params.toString();
        navigate(newSearch ? `${path}?${newSearch}` : path, options);
      } else {
        // Relative URL: merge current params behind any params already in `to`.
        const [path, qs] = to.split('?');
        const params = new URLSearchParams(qs ?? '');
        if (view && !params.has('view')) params.set('view', view);
        if (year && !params.has('year')) params.set('year', year);
        const newSearch = params.toString();
        navigate(newSearch ? `${path}?${newSearch}` : path, options);
      }
    },
    [navigate, search]
  );
};

export default useNavigateKeepingSearch;
